// GENERATED by kanban-board-be `npm run contract:export`. Do not edit.
// Source of truth: kanban-board-be/src/contract. Change it there and re-export.

import { z } from "zod";

/**
 * The entity shapes that go over the wire.
 *
 * These are deliberately not derived from the Drizzle tables. A table is how a
 * row is stored; this is what a client is allowed to see, and the two should be
 * free to differ. `users.passwordHash` is the clearest case — it exists in the
 * table, and if this file were generated from the table it would exist here
 * too, one careless `select *` away from being broadcast to every connected
 * browser. Writing the wire shape by hand means a column is private until
 * somebody deliberately publishes it.
 */

const uuid = z.uuid();
const timestamp = z.iso.datetime({ offset: true });

/** Present on every synced entity; the client's replica keys off these. */
const syncFields = {
  id: uuid,
  workspaceId: uuid,
  version: z.number().int(),
  createdAt: timestamp,
  updatedAt: timestamp,
  deletedAt: timestamp.nullable(),
};

export const UserRoleSchema = z.enum(["member", "manager", "director", "admin"]);
export type UserRole = z.infer<typeof UserRoleSchema>;
export const UserStatusSchema = z.enum(["invited", "active", "deactivated"]);

export const UserSchema = z.object({
  ...syncFields,
  email: z.email(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  role: UserRoleSchema,
  status: UserStatusSchema,
  slackUserId: z.string().nullable(),
  // passwordHash is intentionally absent. See the note above.
});
export type User = z.infer<typeof UserSchema>;

export const ProjectStatusSchema = z.enum([
  "planned",
  "active",
  "paused",
  "completed",
  "cancelled",
]);

export const ProjectSchema = z.object({
  ...syncFields,
  name: z.string(),
  key: z.string(),
  description: z.string().nullable(),
  status: ProjectStatusSchema,
  leadId: uuid.nullable(),
  startDate: z.string().nullable(),
  targetDate: z.string().nullable(),
  position: z.string(),
  isPrivate: z.boolean(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const ProjectMemberSchema = z.object({
  ...syncFields,
  projectId: uuid,
  userId: uuid,
});
export type ProjectMember = z.infer<typeof ProjectMemberSchema>;

export const WorkflowStateTypeSchema = z.enum([
  "backlog",
  "unstarted",
  "started",
  "completed",
  "cancelled",
]);

export const WorkflowStateSchema = z.object({
  ...syncFields,
  projectId: uuid.nullable(),
  name: z.string(),
  type: WorkflowStateTypeSchema,
  color: z.string(),
  position: z.string(),
});
export type WorkflowState = z.infer<typeof WorkflowStateSchema>;

export const TaskPrioritySchema = z.enum(["urgent", "high", "medium", "low", "none"]);

export const TaskSchema = z.object({
  ...syncFields,
  projectId: uuid,
  number: z.number().int(),
  title: z.string(),
  description: z.string().nullable(),
  stateId: uuid,
  assigneeId: uuid.nullable(),
  creatorId: uuid.nullable(),
  priority: TaskPrioritySchema,
  estimate: z.number().nullable(),
  position: z.string(),
  dueDate: z.string().nullable(),
  startedAt: timestamp.nullable(),
  completedAt: timestamp.nullable(),
  parentId: uuid.nullable(),
  sourceSuggestionId: uuid.nullable(),
  // searchVector is a storage detail and never leaves the server.
});
export type Task = z.infer<typeof TaskSchema>;

export const LabelSchema = z.object({
  ...syncFields,
  name: z.string(),
  color: z.string(),
});
export type Label = z.infer<typeof LabelSchema>;

export const TaskLabelSchema = z.object({
  ...syncFields,
  taskId: uuid,
  labelId: uuid,
});
export type TaskLabel = z.infer<typeof TaskLabelSchema>;

export const CommentSchema = z.object({
  ...syncFields,
  taskId: uuid,
  authorId: uuid.nullable(),
  body: z.string(),
  parentId: uuid.nullable(),
  slackChannelId: z.string().nullable(),
  slackMessageTs: z.string().nullable(),
});
export type Comment = z.infer<typeof CommentSchema>;

export const ActivityTypeSchema = z.enum([
  "task_created",
  "task_state_changed",
  "task_assigned",
  "task_unassigned",
  "task_priority_changed",
  "task_due_date_changed",
  "task_title_changed",
  "task_moved_project",
  "task_deleted",
  "task_restored",
  "label_added",
  "label_removed",
  "comment_added",
  "suggestion_confirmed",
]);

export const ActivitySchema = z.object({
  ...syncFields,
  taskId: uuid.nullable(),
  projectId: uuid.nullable(),
  actorId: uuid.nullable(),
  type: ActivityTypeSchema,
  data: z.record(z.string(), z.unknown()),
});
export type Activity = z.infer<typeof ActivitySchema>;

export const NotificationTypeSchema = z.enum([
  "task_assigned",
  "task_unassigned",
  "task_commented",
  "comment_replied",
  "mentioned",
  "task_state_changed",
  "task_priority_urgent",
  "task_due_soon",
  "task_overdue",
  "task_deleted",
  "project_added",
  "suggestion_pending",
  "suggestion_confirmed",
  /** Preferences only: the morning summary DM. Never an inbox row. */
  "daily_digest",
  /** Preferences only: the Monday summary DM for managers, directors and admins. */
  "weekly_summary",
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/**
 * Whether a type is sent to Slack for someone who hasn't chosen.
 *
 * Shared by the notification worker and the settings page so the switch
 * shows what actually happens. The things a person needs to act on reach
 * Slack; the chatter (every comment on a followed task, every reassignment
 * away) stays in the inbox unless they ask for it. Due and overdue are in
 * the morning summary instead: the reminders are written as the day turns
 * over, and a DM at midnight helps nobody.
 */
export const SLACK_DEFAULTS: Record<NotificationType, boolean> = {
  task_assigned: true,
  task_unassigned: false,
  task_commented: false,
  comment_replied: true,
  mentioned: true,
  task_state_changed: false,
  task_priority_urgent: true,
  task_due_soon: false,
  task_overdue: false,
  task_deleted: false,
  project_added: false,
  suggestion_pending: true,
  suggestion_confirmed: false,
  daily_digest: true,
  weekly_summary: true,
};

export const NotificationSchema = z.object({
  ...syncFields,
  userId: uuid,
  type: NotificationTypeSchema,
  taskId: uuid.nullable(),
  actorId: uuid.nullable(),
  data: z.record(z.string(), z.unknown()),
  readAt: timestamp.nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationPreferenceSchema = z.object({
  ...syncFields,
  userId: uuid,
  type: NotificationTypeSchema,
  inApp: z.boolean(),
  slack: z.boolean(),
});
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

export const TaskSubscriptionSchema = z.object({
  ...syncFields,
  taskId: uuid,
  userId: uuid,
  subscribed: z.boolean(),
});
export type TaskSubscription = z.infer<typeof TaskSubscriptionSchema>;

/**
 * Every synced entity, keyed by wire table name. The client uses this to
 * validate incoming delta rows before they touch its local store — a malformed
 * row rejected at the boundary is a logged warning, while the same row written
 * through is a replica that stays wrong until site data is cleared.
 */
export const ENTITY_SCHEMAS = {
  users: UserSchema,
  projects: ProjectSchema,
  projectMembers: ProjectMemberSchema,
  workflowStates: WorkflowStateSchema,
  labels: LabelSchema,
  tasks: TaskSchema,
  taskLabels: TaskLabelSchema,
  comments: CommentSchema,
  activity: ActivitySchema,
  taskSubscriptions: TaskSubscriptionSchema,
  notifications: NotificationSchema,
  notificationPreferences: NotificationPreferenceSchema,
} as const;
